/**
 * MailboxDirectoryPanel — one panel for every mailbox, both providers.
 *
 * Replaces `MicrosoftSetupWizard` + `GoogleMailboxSetupWizard`, which read two
 * unrelated stores through two unrelated shapes and disagreed about almost
 * everything: the stat cards were labelled "Approved / Pending / Expired ·
 * Revoked" on one and "Active / Pending / Needs Reauth" on the other, the rows
 * were rendered by two different components with different action sets, and the
 * Gmail half silently scoped itself to `inboxes[0]` — so a mailbox attached to
 * any other inbox rendered as "No Gmail mailboxes attached to this inbox yet"
 * while its grant was live and its ref row existed.
 *
 * Each row therefore carries TWO badges, provider and inbox, because "which
 * inbox is this feeding?" was the question the old panels could not answer. A
 * mailbox attached to nothing shows "Not attached" rather than vanishing —
 * previously indistinguishable from never having been connected.
 */
import React, { useState } from 'react';
import { AlertTriangle, Info, Loader2, Mail, Send, ShieldCheck, X } from 'lucide-react';

import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { StatusBadge } from './MailboxEmailInput';
import { mailboxBucket, mailboxCounts } from '../../../utils/mailboxDirectory';

const PROVIDER_LABEL = { google: 'Google', microsoft: 'Microsoft' };

function ProviderBadge({ provider }) {
    if (!provider) return null;
    const tone =
        provider === 'google'
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-blue-50 text-blue-700 border-blue-200';
    return (
        <span
            className={`px-1.5 py-0.5 text-xs rounded border ${tone}`}
            data-testid={`mailbox-provider-${provider}`}
        >
            {PROVIDER_LABEL[provider] || provider}
        </span>
    );
}

function InboxBadges({ inboxes }) {
    const list = Array.isArray(inboxes) ? inboxes : [];
    if (list.length === 0) {
        return (
            <span
                className="px-1.5 py-0.5 text-xs rounded border border-dashed border-stone-300 text-stone-400"
                data-testid="mailbox-inbox-none"
            >
                Not attached
            </span>
        );
    }
    return (
        <>
            {list.map(inbox => (
                <span
                    key={inbox.inbox_id}
                    className="px-1.5 py-0.5 text-xs rounded border border-stone-200 bg-stone-50 text-stone-600"
                    data-testid={`mailbox-inbox-${inbox.inbox_id}`}
                    title={inbox.name}
                >
                    {inbox.name || inbox.inbox_id}
                </span>
            ))}
        </>
    );
}

// `testId` is explicit rather than derived from `label`. Deriving it meant
// rewording a card silently moved its test hook — renaming "Revoked" to
// "Revoked / Not requested" turned `mailbox-stat-revoked` into
// `mailbox-stat-revokednotrequested`, which no test noticed because none
// referenced it. A display string is not an identifier.
function StatCard({ label, testId, count, tone, onClick, active }) {
    const tones = {
        emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        amber: 'bg-amber-50 text-amber-800 border-amber-200',
        red: 'bg-red-50 text-red-800 border-red-200',
        stone: 'bg-stone-50 text-stone-600 border-stone-200',
    };
    return (
        <button
            type="button"
            className={`flex flex-col items-center gap-1 p-4 rounded-lg border ${
                tones[tone] || tones.stone
            } cursor-pointer hover:shadow-sm transition-shadow ${
                active ? 'ring-2 ring-offset-1 ring-stone-400' : ''
            }`}
            onClick={onClick}
            data-testid={`mailbox-stat-${testId}`}
        >
            <span className="text-2xl font-semibold">{count}</span>
            <span className="text-xs">{label}</span>
        </button>
    );
}

export default function MailboxDirectoryPanel({
    rows = [],
    loading = false,
    onRequestAccess,
    onResend,
    onCancel,
    onRemove,
    onResendAll,
    isBusy = false,
}) {
    const [filter, setFilter] = useState(null);
    const [confirming, setConfirming] = useState(null);

    const counts = mailboxCounts(rows);
    const visible = filter ? rows.filter(r => mailboxBucket(r) === filter) : rows;
    const toggle = bucket => setFilter(prev => (prev === bucket ? null : bucket));

    return (
        <div className="p-6 overflow-y-auto" data-testid="mailbox-directory-panel">
            <Card className="max-w-3xl mx-auto p-6">
                <div className="mb-6">
                    <h2
                        className="text-lg font-semibold text-stone-900 flex items-center gap-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        <ShieldCheck className="w-5 h-5 text-stone-500" />
                        Connect mailboxes
                    </h2>
                    <p className="text-sm text-stone-500 mt-1">
                        Every mailbox NurtureCRM holds credentials for, and the inboxes
                        each one feeds. Access is granted per person, not tenant-wide —
                        the owner approves their own mailbox from an emailed consent
                        link.
                    </p>
                </div>

                {/*
                 * Both provider caveats, kept because each describes a failure the
                 * admin can only prevent from this screen. The Entra one fails for
                 * the whole tenant at once: without a one-time admin consent NO owner
                 * can approve, and every consent link dead-ends on Microsoft's
                 * "Approval required" page — visible otherwise only to the owners
                 * receiving the invites.
                 */}
                <div
                    className="flex gap-2.5 mb-6 p-3 rounded-lg border border-stone-200 bg-stone-50"
                    data-testid="mailbox-directory-consent-note"
                >
                    <Info className="w-4 h-4 text-stone-400 shrink-0 mt-0.5" />
                    <div className="text-xs text-stone-600 leading-relaxed space-y-1.5">
                        <p>
                            <span className="font-medium text-stone-800">
                                Microsoft 365 — one-time setup by a Microsoft admin.
                            </span>{' '}
                            Before owners can approve, someone with Global Administrator
                            or Cloud Application Administrator must grant admin consent
                            for NurtureCRM (Entra admin centre › Enterprise applications
                            › NurtureCRM › Permissions). Without it every consent link
                            shows “Approval required”.
                        </p>
                        <p>
                            <span className="font-medium text-stone-800">
                                Google — owner consent only.
                            </span>{' '}
                            You cannot connect a Gmail mailbox on someone's behalf. The
                            consent link opens Google's OAuth flow in the owner's own
                            session; once approved the mailbox attaches to the inbox it
                            was requested for.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <StatCard
                        label="Active"
                        testId="active"
                        count={counts.active}
                        tone="emerald"
                        onClick={() => toggle('active')}
                        active={filter === 'active'}
                    />
                    <StatCard
                        label="Pending"
                        testId="pending"
                        count={counts.pending}
                        tone="amber"
                        onClick={() => toggle('pending')}
                        active={filter === 'pending'}
                    />
                    <StatCard
                        label="Needs Reauth"
                        testId="reauth"
                        count={counts.reauth}
                        tone="red"
                        onClick={() => toggle('reauth')}
                        active={filter === 'reauth'}
                    />
                    {/*
                     * Labelled for both, because the bucket holds both: `revoked`
                     * (a credential that was torn down) and `none` (an address
                     * typed into Members that was never requested). Neither has a
                     * working credential, so they share a filter — but a card
                     * reading only "Revoked" over a list containing "Not
                     * Requested" badges misdescribes its own contents.
                     */}
                    <StatCard
                        label="Revoked / Not requested"
                        testId="revoked"
                        count={counts.revoked}
                        tone="stone"
                        onClick={() => toggle('revoked')}
                        active={filter === 'revoked'}
                    />
                    {/*
                     * Carried over from the Microsoft wizard rather than dropped
                     * with it. Chasing several stalled consents one at a time is
                     * the case this existed for, and it is provider-agnostic.
                     */}
                    {counts.pending >= 2 && onResendAll && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="ml-auto"
                            onClick={onResendAll}
                            disabled={isBusy}
                            data-testid="mailbox-resend-all-pending"
                        >
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                            Resend all pending
                        </Button>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-stone-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading mailboxes…
                    </div>
                ) : visible.length === 0 ? (
                    <div className="py-6 text-center" data-testid="mailbox-directory-empty">
                        <p className="text-sm text-stone-500">
                            {filter
                                ? `No ${filter} mailboxes.`
                                : 'No mailboxes yet. Add addresses in an inbox’s Members tab to request access.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {filter && (
                            <div className="mb-2">
                                <button
                                    type="button"
                                    className="text-xs text-stone-500 hover:text-stone-700 underline"
                                    onClick={() => setFilter(null)}
                                    data-testid="mailbox-directory-show-all"
                                >
                                    Show all
                                </button>
                            </div>
                        )}
                        <div className="space-y-2">
                            {visible.map(row => (
                                <div
                                    key={row.key || row.email}
                                    className="flex items-center gap-2 p-2 border border-stone-200 rounded-lg flex-wrap"
                                    data-testid={`mailbox-row-${row.email}`}
                                >
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-stone-100 text-stone-500 flex-shrink-0">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="block text-sm text-stone-800 truncate">
                                            {row.email}
                                        </span>
                                        <span className="flex items-center gap-1.5 mt-1 flex-wrap">
                                            <ProviderBadge provider={row.provider} />
                                            <InboxBadges inboxes={row.inboxes} />
                                        </span>
                                        {/*
                                         * "Active" says the credential works, not that
                                         * mail is arriving. A Gmail mailbox with no
                                         * Pub/Sub watch is exactly that: healthy,
                                         * attached, and silent — which is the state
                                         * that produced an inbox reading "1 mailbox
                                         * pooled" over an empty message list.
                                         */}
                                        {row.status === 'active' && !row.delivery_live && (
                                            <span
                                                className="flex items-center gap-1 text-xs text-amber-600 mt-1"
                                                data-testid={`mailbox-idle-${row.email}`}
                                            >
                                                <AlertTriangle className="w-3 h-3" />
                                                Connected but not yet receiving — delivery
                                                is being set up.
                                            </span>
                                        )}
                                    </div>
                                    <StatusBadge status={row.status} />

                                    {(row.status === 'none' ||
                                        row.status === 'revoked' ||
                                        row.status === 'reauth') &&
                                        onRequestAccess && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    onRequestAccess(row.email, row.provider)
                                                }
                                                disabled={isBusy}
                                                data-testid={`mailbox-request-${row.email}`}
                                            >
                                                Request access
                                            </Button>
                                        )}
                                    {row.status === 'pending' && onResend && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onResend(row.email, row.provider)}
                                            disabled={isBusy}
                                            data-testid={`mailbox-resend-${row.email}`}
                                        >
                                            Resend
                                        </Button>
                                    )}
                                    {row.status === 'pending' && onCancel && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onCancel(row.email, row.provider)}
                                            disabled={isBusy}
                                            data-testid={`mailbox-cancel-${row.email}`}
                                        >
                                            Cancel
                                        </Button>
                                    )}

                                    {/*
                                     * The X is destructive and irreversible from this
                                     * side, so it confirms inline rather than firing on
                                     * the first click.
                                     *
                                     * Offered only for rows that HAVE a credential.
                                     * `none` is a bare member entry — nothing to tear
                                     * down. `pending` is a sent consent invite with no
                                     * mailbox row yet, so removal could only fail
                                     * (`connection_id_required` for Google, 404 for
                                     * Microsoft); its remedy is the Cancel button beside
                                     * it, which withdraws the invite. Offering an X that
                                     * is guaranteed to error is worse than offering none.
                                     */}
                                    {row.status !== 'none' &&
                                        row.status !== 'pending' &&
                                        onRemove &&
                                        (confirming === row.key ? (
                                            <span className="flex items-center gap-1">
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => {
                                                        onRemove(row);
                                                        setConfirming(null);
                                                    }}
                                                    disabled={isBusy}
                                                    data-testid={`mailbox-remove-confirm-${row.email}`}
                                                >
                                                    Remove &amp; delete token
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setConfirming(null)}
                                                    data-testid={`mailbox-remove-cancel-${row.email}`}
                                                >
                                                    Keep
                                                </Button>
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="p-1 text-stone-400 hover:text-red-600 rounded"
                                                onClick={() => setConfirming(row.key)}
                                                disabled={isBusy}
                                                aria-label={`Remove ${row.email}`}
                                                title="Detach from all inboxes and delete the stored token"
                                                data-testid={`mailbox-remove-${row.email}`}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        ))}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
